import { Container, ITextStyle, Text, TextStyleAlign } from "pixi.js"

import { DEFAULT_FONT_FAMILY, DEFAULT_TEXT_STYLE, TEXT_ROLES, TEXT_SIZES, TEXT_VARIANTS, TextRole, TextSize, TextVariant, TypographyStyle, resolveLegacyTextVariant } from "./typography"

export { TextSize, TextVariant, TextRole } from "./typography"

export interface TextProps {
    size?: TextSize
}

export class TextRenderer {
    _color: number
    _namedSize: TextSize
    _size: number
    _align: TextStyleAlign
    _fontFamily: string
    _bold: boolean
    _resolution?: number
    _wordWrapWidth?: number
    _strokeThickness?: number

    private preset: TypographyStyle = DEFAULT_TEXT_STYLE

    // Explicit overrides always take precedence over a role or legacy variant, regardless of
    // builder call order. Among explicit size()/fontSize() calls, the last call wins.
    size = (size: TextSize): TextRenderer => {
        this._namedSize = size
        this._size = TEXT_SIZES[size]
        return this
    }

    color = (color: number): TextRenderer => {
        if (color !== undefined) {
            this._color = color
        }
        return this
    }

    bold = (enabled = true): TextRenderer => {
        this._bold = enabled
        return this
    }

    align = (align: TextStyleAlign): TextRenderer => {
        this._align = align
        return this
    }

    fontFamily = (fontFamily: string): TextRenderer => {
        this._fontFamily = fontFamily
        return this
    }

    fontSize = (size: number): TextRenderer => {
        this._size = size
        return this
    }

    // Wrap onto multiple lines at `width`, breaking inside a word if one is longer than that. Set
    // at BUILD time rather than by poking text.style afterwards, so the measured width of the
    // resulting Text is guaranteed to respect it — a caller that sizes a panel around the text
    // (see gui/roster_speech.ts) depends on that being true from the first frame.
    wordWrap = (width: number): TextRenderer => {
        this._wordWrapWidth = width
        return this
    }

    // Outline width, in pixels, of the black stroke every Text gets by default (see render()). The
    // default earns its keep over the world, where a label has to stay readable against whatever
    // tile it happens to sit on -- but it is a liability on flat UI: a DARK glyph on a LIGHT fill
    // grows a black halo the same colour as its own ink, which fattens the stems and closes up the
    // counters of small bold type. Pass 0 for text that sits on a solid panel.
    strokeThickness = (thickness: number): TextRenderer => {
        this._strokeThickness = thickness
        return this
    }

    role = (role: TextRole): TextRenderer => {
        this.preset = TEXT_ROLES[role]
        return this
    }

    variant = (variant: TextVariant): TextRenderer => {
        this.preset = TEXT_VARIANTS[variant]
        return this
    }

    // Server-authored dynamic text carries a string. Keep unknown names compatible with the
    // historical default while typed client code uses variant() or role().
    legacyVariant = (name?: string): TextRenderer => {
        this.preset = TEXT_VARIANTS[resolveLegacyTextVariant(name)]
        return this
    }

    render = (text?: string): Container => {
        const style: Partial<ITextStyle> = {
            fontFamily: this._fontFamily || DEFAULT_FONT_FAMILY,
            // font: 'bold italic Sans-Serif',
            fill: "#ffffff",
            // stroke: '#000000',
            fontWeight: (this._bold ?? this.preset.bold) ? "bolder" : "normal",
            align: this._align || "left",
            strokeThickness: this._strokeThickness === undefined ? 1 : this._strokeThickness,
            fontSize: this._size ?? this.preset.fontSize,
            ...(this._wordWrapWidth !== undefined
                ? { wordWrap: true, wordWrapWidth: this._wordWrapWidth, breakWords: true }
                : {}),
        }

        const someText = new Text(text || "", style)
        someText.roundPixels = true
        const resolution = this._resolution ?? (this.preset.resolutionScale === undefined ? undefined
            : this.preset.resolutionScale * (typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1))
        if (resolution !== undefined) {
            // Fixed resolution => Text.autoResolution off => it won't re-rasterize (nor glitch for a
            // frame) when rendered into a RenderTexture of a different resolution, i.e. the PiP.
            someText.resolution = resolution
        }
        someText.tint = this._color ?? this.preset.color ?? 0xffffff
        someText.scale.set(1.0)

        return someText
    }
}
